import { MAX_AVATAR_BYTES } from '../shared/constants';

const SIZE = 256;

async function decode(file: Blob): Promise<CanvasImageSource & { width: number; height: number }> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // Older Safari: fall back to an <img> element.
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      return img;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Center-crops to a square and resizes to 256×256, encoded as WebP (≤ 300 KB).
 * Browsers that can't encode WebP (older Safari) get JPEG instead.
 */
export async function toAvatarBlob(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/') && file.type !== '') throw new Error('Please choose an image file.');
  const img = await decode(file);
  const side = Math.min(img.width, img.height);
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not process the image.');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, SIZE, SIZE);
  if ('close' in img && typeof img.close === 'function') img.close();

  for (const quality of [0.86, 0.75, 0.6, 0.45, 0.3]) {
    let blob = await toBlob(canvas, 'image/webp', quality);
    if (blob && blob.type !== 'image/webp') blob = await toBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= MAX_AVATAR_BYTES) return blob;
  }
  throw new Error('That image is too large even after compressing.');
}
